import { useState, useRef } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal, Eye, Download, Printer } from "lucide-react";
import { colors } from "@/utils/colors";
import { formatCurrency } from "@/utils/currency";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface DistributionProcess {
  id: number;
  uuid: string;
  cp_classification_id: number;
  from_unity_id?: number;
  unity_id: number;
  quota_quantity: number;
  from_seller_id?: number;
  seller_id?: number;
  distribution_type_id: number;
  distribution_process_status_id: number;
  created_at: string;
  updated_at: string;
  processed_at?: string;
  created_by: {
    id: number;
    name: string;
  };
  updated_by?: {
    id: number;
    name: string;
  };
  seller?: {
    id: number;
    uuid: string;
    user: {
      id: number;
      name: string;
    };
  };
  from_seller?: {
    id: number;
    uuid: string;
    user: {
      id: number;
      name: string;
    };
  };
  unity: {
    id: number;
    name: string;
  };
  from_unity?: {
    id: number;
    name: string;
  };
  contest_product_classification: {
    id: number;
    product_name: string;
    contest: {
      id: number;
      name: string;
    };
    lottery: {
      id: number;
      name: string;
      color: string;
    };
    product_classification: {
      id: number;
      name: string;
      quota_quantity: number;
    };
  };
  distribution_type: {
    id: number;
    name: string;
  };
  distribution_process_status: {
    id: number;
    name: string;
  };
  files?: Array<{
    id: number;
    file_name: string;
    file_url: string;
  }>;
}

interface GroupedDistribution {
  key: string;
  date: string;
  contestId: number;
  contestName: string;
  lotteryName: string;
  lotteryColor: string;
  unityName: string;
  sellerName: string;
  totalQuantity: number;
  totalValue: number;
  status: string;
  statusId: number;
  items: DistributionProcess[];
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  data: DistributionProcess[];
}

interface TableDataProps {
  dataApi: PaginationData;
  pageActions: {
    previous: () => void;
    next: () => void;
    lastPage: () => void;
  };
}

export const TableData = ({ dataApi, pageActions }: TableDataProps) => {
  const [selectedGroup, setSelectedGroup] = useState<GroupedDistribution | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptGroup, setReceiptGroup] = useState<GroupedDistribution | null>(null);
  const receiptRef = useRef<HTMLDivElement>(null);

  const getValueFromClassification = (process: DistributionProcess) => {
    const priceMatch =
      process.contest_product_classification.product_classification.name.match(
        /R\$\s*([\d,\.]+)/
      );
    if (priceMatch) {
      const price = parseFloat(priceMatch[1].replace(",", "."));
      return price * process.quota_quantity;
    }
    return 0;
  };

  const groupDistributions = (data: DistributionProcess[]): GroupedDistribution[] => {
    const groups: Record<string, GroupedDistribution> = {};

    data.forEach((process) => {
      const dateKey = format(new Date(process.created_at), "yyyy-MM-dd HH:mm");
      const key = `${dateKey}-${process.contest_product_classification.contest.id}-${process.unity_id}-${process.distribution_process_status_id}`;

      if (!groups[key]) {
        groups[key] = {
          key,
          date: process.created_at,
          contestId: process.contest_product_classification.contest.id,
          contestName: process.contest_product_classification.contest.name,
          lotteryName: process.contest_product_classification.lottery.name,
          lotteryColor: process.contest_product_classification.lottery.color,
          unityName: process.unity.name,
          sellerName: process.from_seller?.user?.name || process.created_by?.name || "-",
          totalQuantity: 0,
          totalValue: 0,
          status: process.distribution_process_status.name,
          statusId: process.distribution_process_status_id,
          items: [],
        };
      }

      groups[key].totalQuantity += process.quota_quantity;
      groups[key].totalValue += getValueFromClassification(process);
      groups[key].items.push(process);
    });

    return Object.values(groups).sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  };

  const getProcessStatusBadge = (statusName: string, _statusId: number) => {
    const colorMap: Record<string, string> = {
      Pendente: "bg-yellow-500",
      Aceito: "bg-green-500",
      Rejeitado: "bg-red-500",
    };
    return (
      <Badge
        className={cn("text-white", colorMap[statusName] || "bg-gray-500")}
      >
        {statusName}
      </Badge>
    );
  };

  // TODO: Implementar botão para abrir detalhes usando esta função
  void function handleOpenDetails(group: GroupedDistribution) {
    setSelectedGroup(group);
    setShowDetailsModal(true);
  };

  const handleOpenReceipt = (group: GroupedDistribution) => {
    setReceiptGroup(group);
    setShowReceiptModal(true);
  };

  const handlePrint = () => {
    if (receiptRef.current) {
      const printContent = receiptRef.current.innerHTML;
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Comprovante de Distribuição</title>
              <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: Arial, sans-serif; padding: 20px; background: #f5f5f5; }
                .receipt-container { max-width: 400px; margin: 0 auto; background: white; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
                .receipt-header { background: #FDF6ED; padding: 24px; text-align: center; }
                .logo-area { margin-bottom: 16px; }
                .logo-text { color: #023859; font-size: 28px; font-weight: bold; }
                .logo-sub { color: #F97316; font-size: 24px; font-weight: bold; }
                .seller-name { color: #023859; font-size: 18px; font-weight: bold; margin-top: 12px; }
                .unity-badge { display: inline-block; background: #e5e7eb; color: #374151; padding: 6px 16px; border-radius: 20px; font-size: 14px; margin-top: 8px; }
                .receipt-body { padding: 24px; }
                .details-box { background: #FFF7ED; border: 2px solid #FDBA74; border-radius: 12px; padding: 16px; }
                .details-title { font-weight: bold; font-size: 14px; text-align: center; margin-bottom: 8px; color: #1f2937; }
                .details-subtitle { font-size: 12px; text-align: center; color: #6b7280; margin-bottom: 12px; }
                .product-item { border-bottom: 1px dashed #FDBA74; padding: 12px 0; }
                .product-item:last-child { border-bottom: none; }
                .product-name { font-weight: 600; font-size: 14px; text-align: center; color: #1f2937; }
                .product-details { font-size: 12px; color: #6b7280; margin-top: 4px; }
                .product-row { display: flex; justify-content: space-between; margin-top: 4px; }
                .receipt-footer { text-align: center; padding: 16px 24px 24px; border-top: 1px solid #e5e7eb; }
                .signature-text { font-size: 12px; color: #6b7280; margin-bottom: 8px; }
                .brand-logo { color: #F97316; font-size: 20px; font-weight: bold; }
                .brand-crown { color: #F59E0B; }
                @media print {
                  body { padding: 0; background: white; }
                  .receipt-container { box-shadow: none; }
                }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const groupedData = groupDistributions(dataApi?.data || []);

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead className="font-semibold">Data</TableHead>
            <TableHead className="font-semibold">Concurso</TableHead>
            <TableHead className="font-semibold">Vendedor</TableHead>
            <TableHead className="font-semibold">Unidade Destino</TableHead>
            <TableHead className="font-semibold text-center">Produtos</TableHead>
            <TableHead className="font-semibold text-right">Total Cotas</TableHead>
            <TableHead className="font-semibold text-right">Valor Total</TableHead>
            <TableHead className="font-semibold text-center">Status</TableHead>
            <TableHead className="font-semibold text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedData.map((group) => (
            <TableRow key={group.key} className="hover:bg-gray-50 dark:hover:bg-zinc-800">
              <TableCell>
                {format(new Date(group.date), "dd/MM/yyyy HH:mm", {
                  locale: ptBR,
                })}
              </TableCell>
              <TableCell>
                <div>
                  <p className="font-medium">{group.contestName}</p>
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block w-3 h-3 rounded-sm border"
                      style={{
                        backgroundColor: group.lotteryColor || "#6B7280",
                        borderColor: "#d1d5db",
                      }}
                    />
                    <span className="text-xs text-gray-500">{group.lotteryName}</span>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <p className="text-sm">{group.sellerName}</p>
              </TableCell>
              <TableCell>
                <p className="text-sm">{group.unityName}</p>
              </TableCell>
              <TableCell className="text-center">
                <Badge variant="outline" className="font-medium">
                  {group.items.length} {group.items.length === 1 ? "produto" : "produtos"}
                </Badge>
              </TableCell>
              <TableCell className="text-right font-medium">
                {group.totalQuantity}
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(group.totalValue)}
              </TableCell>
              <TableCell className="text-center">
                {getProcessStatusBadge(group.status, group.statusId)}
              </TableCell>
              <TableCell className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleOpenReceipt(group)}
                    className="h-8 w-8 bg-gray-800 hover:bg-gray-700 text-white rounded-full"
                    title="Ver comprovante"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {groupedData.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-8">
                <p className="text-gray-500">Nenhuma distribuição encontrada</p>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-between mb-3 mt-4">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Distribuição</DialogTitle>
            <DialogDescription>
              {selectedGroup && (
                <div className="flex flex-wrap gap-4 mt-2">
                  <span className="text-sm">
                    <strong>Data:</strong> {format(new Date(selectedGroup.date), "dd/MM/yyyy HH:mm")}
                  </span>
                  <span className="text-sm">
                    <strong>Concurso:</strong> {selectedGroup.contestName}
                  </span>
                  <span className="text-sm">
                    <strong>Unidade:</strong> {selectedGroup.unityName}
                  </span>
                  <span className="text-sm">
                    <strong>Vendedor:</strong> {selectedGroup.sellerName}
                  </span>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedGroup && (
            <div className="mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead>Classificação</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-center">Arquivos</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedGroup.items.map((item) => (
                    <TableRow key={item.uuid}>
                      <TableCell className="font-medium">{item.id}</TableCell>
                      <TableCell>
                        {item.contest_product_classification.product_name}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {item.contest_product_classification.product_classification.name}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {item.quota_quantity}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(getValueFromClassification(item))}
                      </TableCell>
                      <TableCell className="text-center">
                        {getProcessStatusBadge(
                          item.distribution_process_status.name,
                          item.distribution_process_status_id
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.files && item.files.length > 0 ? (
                          item.files.length === 1 ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => window.open(item.files![0].file_url, "_blank")}
                              title={item.files[0].file_name || "Baixar arquivo"}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  title={`${item.files.length} arquivos`}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {item.files.map((file) => (
                                  <DropdownMenuItem
                                    key={file.id}
                                    onClick={() => window.open(file.file_url, "_blank")}
                                    className="cursor-pointer"
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    {file.file_name || `Arquivo ${file.id}`}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Total de produtos: <strong>{selectedGroup.items.length}</strong>
                    </span>
                  </div>
                  <div className="flex gap-6">
                    <span className="text-sm">
                      Total cotas: <strong>{selectedGroup.totalQuantity}</strong>
                    </span>
                    <span className="text-sm">
                      Valor total: <strong className="text-green-600">{formatCurrency(selectedGroup.totalValue)}</strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Comprovante de Distribuição</DialogTitle>
          </DialogHeader>

          <Button
            onClick={handlePrint}
            className="w-full bg-[#023859] hover:bg-[#012840] text-white mb-4"
          >
            <Printer className="h-4 w-4 mr-2" />
            Imprimir Comprovante
          </Button>

          <div ref={receiptRef}>
            {receiptGroup && (
              <div className="receipt-container max-w-[400px] mx-auto bg-white rounded-2xl overflow-hidden shadow-lg">
                <div
                  className="receipt-header text-center p-6"
                  style={{
                    background: "#FDF6ED",
                  }}
                >
                  <div className="logo-area mb-4 flex justify-center">
                    <img src="/images/logo-color.png" alt="Loteria Aldeota" className="h-16 object-contain" />
                  </div>
                  <div className="seller-name text-[#023859] text-lg font-bold">
                    VENDEDOR(A): {receiptGroup.sellerName.toUpperCase()}
                  </div>
                  <div className="unity-badge inline-block bg-gray-200 text-gray-700 px-4 py-1.5 rounded-full text-sm mt-2">
                    PARA UNIDADE {receiptGroup.unityName.toUpperCase()}
                  </div>
                </div>

                <div className="receipt-body p-6">
                  <div
                    className="details-box rounded-xl p-4"
                    style={{
                      background: "#FFF7ED",
                      border: "2px solid #FDBA74",
                    }}
                  >
                    <div className="details-title font-bold text-sm text-center mb-2">
                      DETALHES DA DISTRIBUIÇÃO
                    </div>
                    <div className="details-subtitle text-xs text-center text-gray-500 mb-3">
                      Produtos e valores
                    </div>

                    {receiptGroup.items.map((item, index) => (
                      <div
                        key={item.uuid}
                        className="product-item py-3"
                        style={{
                          borderBottom:
                            index < receiptGroup.items.length - 1
                              ? "1px dashed #FDBA74"
                              : "none",
                        }}
                      >
                        <div className="product-name font-semibold text-sm text-center">
                          {item.contest_product_classification.product_name} -{" "}
                          {item.contest_product_classification.product_classification.name}
                        </div>
                        <div className="product-details text-xs text-gray-500 mt-2 space-y-1">
                          <div className="product-row flex justify-between">
                            <span>Qtd: {item.quota_quantity}</span>
                            <span>{formatCurrency(getValueFromClassification(item))}</span>
                          </div>
                          <div>Concurso: {receiptGroup.contestName}</div>
                          <div>Loteria: {receiptGroup.lotteryName}</div>
                        </div>
                      </div>
                    ))}

                    <div
                      className="mt-3 pt-3"
                      style={{ borderTop: "2px solid #FDBA74" }}
                    >
                      <div className="flex justify-between font-bold text-sm">
                        <span>Total Cotas:</span>
                        <span>{receiptGroup.totalQuantity}</span>
                      </div>
                      <div className="flex justify-between font-bold text-sm text-green-600 mt-1">
                        <span>Valor Total:</span>
                        <span>{formatCurrency(receiptGroup.totalValue)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="receipt-footer text-center p-4 border-t border-gray-200">
                  <div className="signature-text text-xs text-gray-500 mb-2">
                    ASSINATURA RESPONSÁVEL
                  </div>
                  <div className="brand-logo text-xl font-bold text-[#F97316]">
                    <span className="brand-crown text-yellow-500">👑</span> REI
                    <span className="text-[#023859]">BOLÃO</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    {format(new Date(receiptGroup.date), "dd/MM/yyyy HH:mm")}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowReceiptModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
